/**
 * The one-visit notice Home shows after a 409 that says the invite is already complete: the
 * activation's (#571 Q8) and the confirmation's `INVITE_COMPLETED` (#682).
 *
 * `goHomeWithCompletedInviteNotice` sets the flag and hard-navigates Home, from the activate-track-id
 * page and from the confirm page, and Home shows the notice and clears the flag. It lives in
 * sessionStorage because the hard navigation drops in-memory state, and Home's no-identity
 * redirect must not use it up before Home shows.
 */
import { NAV_ITEM_HOME } from '@/path';
import { clearSessionInviteFromStorage } from '@/utils/utils';
import { isServer } from '@verifime/utils';
import { useEffect, useState } from 'react';

const COMPLETED_INVITE_NOTICE = 'completedInviteNotice';

/** Marks where the client's name goes in the notice copy. */
export const CLIENT_NAME_SPOT = '{clientName}';

// COPY-571 string 1, supplied by Charles. The marked spot takes the client's name.
export const COMPLETED_INVITE_NOTICE_COPY = `${CLIENT_NAME_SPOT} has already received your identity details. You do not need to do anything further.`;

/** Stands in for the client's name when it cannot be read (COPY-571 string 1, supplied by Charles). */
export const COMPLETED_INVITE_NOTICE_FALLBACK_NAME = 'Your service provider';

export type TCompletedInviteNotice = { clientName: string };

export function completedInviteNoticeText(clientName: string): string {
  return COMPLETED_INVITE_NOTICE_COPY.replace(CLIENT_NAME_SPOT, clientName);
}

// The notice is a courtesy: storage that refuses the write (quota, blocked) must not stop the
// customer reaching Home, so the failure is logged without the error and the notice is dropped.
export function setCompletedInviteNotice(notice: TCompletedInviteNotice) {
  try {
    sessionStorage.setItem(COMPLETED_INVITE_NOTICE, JSON.stringify(notice));
  } catch {
    console.warn('Completed invite: the notice could not be kept');
  }
}

export function clearCompletedInviteNotice() {
  sessionStorage.removeItem(COMPLETED_INVITE_NOTICE);
}

/**
 * The route for an invite the server says is already complete (activation 409, #571; confirmation
 * 409 `INVITE_COMPLETED`, #682): the customer is signed in and owns it, so keep the session and go
 * Home. The notice names the client, or the fallback name when it is not known. The completed
 * reference has nothing left to do, and the wizard and entity pages would read it as the live
 * invite, so the session invite is cleared as a sign-out clears it. Storage that refuses the clear
 * (private mode, blocked) must not stop the customer reaching Home: the failure is logged once,
 * without the error, and the redirect still runs.
 */
export function goHomeWithCompletedInviteNotice(clientName: string | undefined): void {
  setCompletedInviteNotice({ clientName: clientName ?? COMPLETED_INVITE_NOTICE_FALLBACK_NAME });
  try {
    clearSessionInviteFromStorage();
  } catch {
    console.warn('Completed invite: the session invite could not be cleared');
  }
  window.location.replace(NAV_ITEM_HOME.path);
}

function parseNotice(stored: string): TCompletedInviteNotice | null {
  try {
    const { clientName } = JSON.parse(stored) as Partial<TCompletedInviteNotice>;
    return typeof clientName === 'string' && clientName ? { clientName } : null;
  } catch {
    return null;
  }
}

export function getCompletedInviteNotice(): TCompletedInviteNotice | null {
  if (isServer) {
    return null;
  }
  const stored = sessionStorage.getItem(COMPLETED_INVITE_NOTICE);
  return stored ? parseNotice(stored) : null;
}

/**
 * The notice for this Home visit: the flag is read into state and cleared on mount, so the next
 * visit has none. Mount it only where Home itself shows, not while Home loads or redirects, so the
 * flag waits through the no-identity redirect.
 */
export function useCompletedInviteNotice(): TCompletedInviteNotice | null {
  const [notice, setNotice] = useState<TCompletedInviteNotice | null>(null);

  useEffect(() => {
    const stored = getCompletedInviteNotice();
    if (stored) {
      setNotice(stored);
      clearCompletedInviteNotice();
    }
  }, []);

  return notice;
}
